import type {
  FinancialSnapshotDocument,
  FinancialSnapshotTemplate,
  SnapshotCategoryData,
  SnapshotSubCategoryData,
} from '../financial-snapshot.model';
import { MacroCategory } from '../financial-snapshot.model';
import { computeCategoryTotal } from '../compute/financial-snapshot-compute';

/** Flat form values: undefined = blank, 0 = entered zero. */
export type SnapshotFormValues = Record<string, number | undefined>;

/** EDD validation message for income/spending amounts. */
export const AMOUNT_VALIDATION_MESSAGE = 'Enter a valid amount of $0 or more';

/** True when any form amount is a finite number below 0. */
export function hasNegativeAmounts(
  values: SnapshotFormValues,
  otherCustoms: OtherCustomEntry[] = []
): boolean {
  for (const v of Object.values(values)) {
    if (typeof v === 'number' && v < 0) {
      return true;
    }
  }
  for (const custom of otherCustoms) {
    if (typeof custom.value === 'number' && custom.value < 0) {
      return true;
    }
  }
  return false;
}

export function assertNonNegativeAmounts(
  values: SnapshotFormValues,
  otherCustoms: OtherCustomEntry[] = []
): void {
  if (hasNegativeAmounts(values, otherCustoms)) {
    throw new Error(AMOUNT_VALIDATION_MESSAGE);
  }
}

/** True when value is a finite number >= 0 (blank/NaN excluded). */
export function isNonNegativeAmount(value: number | null | undefined): value is number {
  return typeof value === 'number' && !Number.isNaN(value) && value >= 0;
}

/** One FA-editable custom subcategory under `other` (arbitrary key + label). */
export interface OtherCustomEntry {
  key: string;
  label: string;
  value: number | undefined;
}

export const OTHER_NAMED = ['activities', 'personal', 'medical'] as const;
export type OtherNamedKey = (typeof OTHER_NAMED)[number];

/** Max length for custom Other labels (EDD SEC-3). */
export const OTHER_CUSTOM_LABEL_MAX_LEN = 80;

export function categorySubKey(catKey: string, subKey: string): string {
  return `${catKey}.${subKey}`;
}

export function otherSubKey(subKey: string): string {
  return `other.${subKey}`;
}

/**
 * Form key for a category total entered directly (no breakdown).
 * The API accepts either an itemized `subCategories` map or a bare `totalValue`.
 */
export function categoryTotalKey(catKey: string): string {
  return `${catKey}.totalValue`;
}

export const SAVINGS_KEY = categoryTotalKey('savings');

export function isOtherNamedKey(key: string): boolean {
  return (OTHER_NAMED as readonly string[]).includes(key);
}

/** Stable unique key for FA-created custom Other rows. */
export function newOtherCustomKey(): string {
  const id =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  return `custom_${id}`;
}

export function sanitizeOtherCustomLabel(raw: string): string {
  return raw.trim().slice(0, OTHER_CUSTOM_LABEL_MAX_LEN);
}

/** Load all non-named `other.subCategories` (preserve keys + labels). */
export function initOtherCustoms(
  snapshot: FinancialSnapshotDocument | null | undefined
): OtherCustomEntry[] {
  const otherSubs = snapshot?.categories?.['other']?.subCategories ?? {};
  const customs: OtherCustomEntry[] = [];
  for (const [k, entry] of Object.entries(otherSubs)) {
    if (isOtherNamedKey(k)) {
      continue;
    }
    const sub = entry as SnapshotSubCategoryData;
    customs.push({
      key: k,
      label: typeof sub.label === 'string' ? sub.label : '',
      value: typeof sub.value === 'number' ? sub.value : undefined,
    });
  }
  return customs;
}

/** Initialize form from snapshot (blank vs 0 preserved). Named Other only — customs via {@link initOtherCustoms}. */
export function initFormValues(
  snapshot: FinancialSnapshotDocument | null | undefined,
  template: FinancialSnapshotTemplate
): SnapshotFormValues {
  const values: SnapshotFormValues = {};
  const cats = snapshot?.categories ?? {};

  for (const [catKey, tmpl] of Object.entries(template)) {
    if (catKey === 'other') {
      for (const sub of OTHER_NAMED) {
        const v = (cats['other']?.subCategories?.[sub] as SnapshotSubCategoryData | undefined)
          ?.value;
        values[otherSubKey(sub)] = typeof v === 'number' ? v : undefined;
      }
      const otherTotal = cats['other']?.totalValue;
      values[categoryTotalKey('other')] = typeof otherTotal === 'number' ? otherTotal : undefined;
      continue;
    }
    if (tmpl.macroCategory === MacroCategory.savings) {
      const tv = cats['savings']?.totalValue;
      values[SAVINGS_KEY] = typeof tv === 'number' ? tv : undefined;
      continue;
    }
    if (tmpl.subCategories) {
      for (const subKey of Object.keys(tmpl.subCategories)) {
        const v = (cats[catKey]?.subCategories?.[subKey] as SnapshotSubCategoryData | undefined)
          ?.value;
        values[categorySubKey(catKey, subKey)] = typeof v === 'number' ? v : undefined;
      }
      const tv = cats[catKey]?.totalValue;
      values[categoryTotalKey(catKey)] = typeof tv === 'number' ? tv : undefined;
    }
  }
  return values;
}

export function formValuesEqual(a: SnapshotFormValues, b: SnapshotFormValues): boolean {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const key of keys) {
    if (a[key] !== b[key]) {
      return false;
    }
  }
  return true;
}

export function otherCustomsEqual(a: OtherCustomEntry[], b: OtherCustomEntry[]): boolean {
  if (a.length !== b.length) {
    return false;
  }
  for (let i = 0; i < a.length; i++) {
    if (a[i].key !== b[i].key || a[i].label !== b[i].label || a[i].value !== b[i].value) {
      return false;
    }
  }
  return true;
}

/**
 * Persistable custom rows: non-empty label required; omit blank amounts; keep explicit 0.
 */
export function persistableOtherCustoms(customs: OtherCustomEntry[]): OtherCustomEntry[] {
  const out: OtherCustomEntry[] = [];
  for (const c of customs) {
    const label = sanitizeOtherCustomLabel(c.label);
    if (!label) {
      continue;
    }
    out.push({
      key: c.key,
      label,
      value: typeof c.value === 'number' ? c.value : undefined,
    });
  }
  return out;
}

/** Itemized subcategory values for one category, from form state. */
function itemizedSubsFromForm(
  catKey: string,
  values: SnapshotFormValues,
  template: FinancialSnapshotTemplate,
  otherCustoms: OtherCustomEntry[] = []
): Record<string, SnapshotSubCategoryData> {
  const subCategories: Record<string, SnapshotSubCategoryData> = {};

  if (catKey === 'other') {
    for (const sub of OTHER_NAMED) {
      const v = values[otherSubKey(sub)];
      if (typeof v === 'number') {
        subCategories[sub] = { value: v };
      }
    }
    // Persist customs with a non-empty label; include value when entered (blank≠0).
    for (const custom of persistableOtherCustoms(otherCustoms)) {
      if (typeof custom.value !== 'number') {
        continue;
      }
      subCategories[custom.key] = { value: custom.value, label: custom.label };
    }
    return subCategories;
  }

  for (const subKey of Object.keys(template[catKey]?.subCategories ?? {})) {
    const v = values[categorySubKey(catKey, subKey)];
    if (typeof v === 'number') {
      subCategories[subKey] = { value: v };
    }
  }
  return subCategories;
}

/** True when the FA has entered at least one itemized amount for the category. */
export function hasItemizedValues(
  catKey: string,
  values: SnapshotFormValues,
  template: FinancialSnapshotTemplate,
  otherCustoms: OtherCustomEntry[] = []
): boolean {
  return (
    Object.keys(itemizedSubsFromForm(catKey, values, template, otherCustoms)).length > 0
  );
}

/**
 * Category payload from form state; null when nothing was entered.
 * Itemized amounts win over a directly entered total (matches API total precedence).
 */
export function buildCategoryFromForm(
  catKey: string,
  values: SnapshotFormValues,
  template: FinancialSnapshotTemplate,
  otherCustoms: OtherCustomEntry[] = []
): SnapshotCategoryData | null {
  const subCategories = itemizedSubsFromForm(catKey, values, template, otherCustoms);
  if (Object.keys(subCategories).length > 0) {
    return { subCategories };
  }
  const total = values[categoryTotalKey(catKey)];
  return typeof total === 'number' ? { totalValue: total } : null;
}

/** Build categories payload from form (omit blank fields; keep explicit 0). */
export function buildCategoriesFromForm(
  values: SnapshotFormValues,
  template: FinancialSnapshotTemplate,
  otherCustoms: OtherCustomEntry[] = []
): Record<string, SnapshotCategoryData> {
  assertNonNegativeAmounts(values, otherCustoms);
  const categories: Record<string, SnapshotCategoryData> = {};

  for (const [catKey, tmpl] of Object.entries(template)) {
    if (tmpl.macroCategory === MacroCategory.savings) {
      const v = values[SAVINGS_KEY];
      if (typeof v === 'number') {
        categories['savings'] = { totalValue: v };
      }
      continue;
    }
    if (catKey !== 'other' && !tmpl.subCategories) {
      continue;
    }
    const data = buildCategoryFromForm(catKey, values, template, otherCustoms);
    if (data) {
      categories[catKey] = data;
    }
  }

  return categories;
}

export function buildDocumentFromForm(
  guid: string,
  values: SnapshotFormValues,
  template: FinancialSnapshotTemplate,
  existing?: FinancialSnapshotDocument | null,
  otherCustoms: OtherCustomEntry[] = []
): FinancialSnapshotDocument {
  return {
    guid,
    createdDate: existing?.createdDate,
    submittedDate: existing?.submittedDate,
    modifiedDate: existing?.modifiedDate,
    lastUpdatedAt: existing?.lastUpdatedAt,
    categories: buildCategoriesFromForm(values, template, otherCustoms),
  };
}

/** Effective category total for the modal: itemized sum, else the directly entered total. */
export function categorySumFromForm(
  catKey: string,
  values: SnapshotFormValues,
  template: FinancialSnapshotTemplate,
  otherCustoms: OtherCustomEntry[] = []
): number | null {
  if (catKey !== 'other' && !template[catKey]?.subCategories) {
    return null;
  }
  const data = buildCategoryFromForm(catKey, values, template, otherCustoms);
  return computeCategoryTotal(data ?? undefined);
}

export function spendingCategoryKeys(template: FinancialSnapshotTemplate): string[] {
  return Object.entries(template)
    .filter(
      ([key, t]) =>
        t.macroCategory === MacroCategory.spending && key !== 'other' && !!t.subCategories
    )
    .sort((a, b) => (a[1].displayOrder ?? a[1].order) - (b[1].displayOrder ?? b[1].order))
    .map(([k]) => k);
}

export function incomeCategoryKey(template: FinancialSnapshotTemplate): string | null {
  const entry = Object.entries(template).find(([, t]) => t.macroCategory === MacroCategory.income);
  return entry?.[0] ?? null;
}

/** Compare category payloads ignoring audit metadata. */
export function normalizeCategoryForCompare(
  cat: SnapshotCategoryData | undefined
): { totalValue?: number; subCategories?: Record<string, { value?: number; label?: string }> } | null {
  if (!cat) {
    return null;
  }
  const subCategories: Record<string, { value?: number; label?: string }> = {};
  for (const [k, v] of Object.entries(cat.subCategories ?? {})) {
    subCategories[k] = { value: v.value, label: v.label };
  }
  return {
    totalValue: cat.totalValue,
    subCategories: Object.keys(subCategories).length ? subCategories : undefined,
  };
}

export function categoryPayloadEqual(
  a: SnapshotCategoryData | undefined,
  b: SnapshotCategoryData | undefined
): boolean {
  return (
    JSON.stringify(normalizeCategoryForCompare(a)) ===
    JSON.stringify(normalizeCategoryForCompare(b))
  );
}

/** Stamp FA audit on every category / subcategory present in the payload. */
export function stampFaAudit(
  categories: Record<string, SnapshotCategoryData>,
  lastEditedById: string,
  at = new Date().toISOString()
): Record<string, SnapshotCategoryData> {
  const out: Record<string, SnapshotCategoryData> = {};
  for (const [key, cat] of Object.entries(categories)) {
    const next: SnapshotCategoryData = {
      ...cat,
      lastEditedAt: at,
      lastEditedBy: 'fa',
      lastEditedById,
    };
    if (cat.subCategories) {
      const subs: Record<string, SnapshotSubCategoryData> = {};
      for (const [sk, sub] of Object.entries(cat.subCategories)) {
        subs[sk] = {
          ...sub,
          lastEditedAt: at,
          lastEditedBy: 'fa',
          lastEditedById,
        };
      }
      next.subCategories = subs;
    }
    out[key] = next;
  }
  return out;
}

/**
 * Categories that held an amount server-side and now hold none.
 *
 * PATCH rejects a category with no subCategories ("At least one subCategory is
 * expected in Category x"), so these are caught before any write is attempted.
 * An explicitly entered 0 is a value and is not counted as cleared.
 */
export function clearedCategoryKeys(
  previous: Record<string, SnapshotCategoryData>,
  next: Record<string, SnapshotCategoryData>
): string[] {
  return Object.keys(previous).filter(
    (key) =>
      computeCategoryTotal(previous[key]) !== null && computeCategoryTotal(next[key]) === null
  );
}

export function changedCategoryKeys(
  previous: Record<string, SnapshotCategoryData>,
  next: Record<string, SnapshotCategoryData>
): string[] {
  const keys = new Set([...Object.keys(previous), ...Object.keys(next)]);
  const changed: string[] = [];
  for (const key of keys) {
    if (!categoryPayloadEqual(previous[key], next[key])) {
      changed.push(key);
    }
  }
  return changed;
}
